import React, {
  useMemo,
  useState,
  useCallback,
  useRef,
  useLayoutEffect,
} from "react";
import { Box, Pagination, TextField, Collapse, Tooltip } from "@mui/material";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { TableHeaderDownArrowIcon, TableHeaderUpArrowIcon } from "./TableIcons";
import { spinAnimation } from "../utils/animations";
import CustomCheckbox from "../Checkbox/CustomCheckbox";
import { colors } from "../theme/colors";
import "./CustomTable.css";

const TOOLTIP_SLOT_PROPS = {
  popper: {
    modifiers: [{ name: "offset", options: { offset: [0, -8] } }],
  },
};

const TruncatedTextWithTooltip: React.FC<{ text: string }> = ({ text }) => {
  return (
    <CellTooltipWrapper text={text}>
      <Box
        component="span"
        data-table-ellipsis
        sx={{
          display: "block",
          minWidth: 0,
          width: "100%",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {text}
      </Box>
    </CellTooltipWrapper>
  );
};

const CellTooltipWrapper: React.FC<{
  text?: string;
  children: React.ReactNode;
}> = ({ text, children }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [tooltipText, setTooltipText] = useState<string | null>(null);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const handleMouseEnter = useCallback(() => {
    const element = ref.current;
    const nextTooltipText = text ?? element?.innerText?.trim() ?? "";
    if (!element || !nextTooltipText) {
      setIsTooltipOpen(false);
      return;
    }
    const ellipsisElement = element.querySelector<HTMLElement>(
      "[data-table-ellipsis]",
    );
    const measuredElements = ellipsisElement
      ? [ellipsisElement]
      : [element, ...Array.from(element.querySelectorAll<HTMLElement>("*"))];
    const isTruncated = measuredElements.some(
      (measuredElement) =>
        measuredElement.clientWidth > 0 &&
        measuredElement.scrollWidth > measuredElement.clientWidth + 1,
    );
    if (isTruncated) {
      setTooltipText(nextTooltipText);
      setIsTooltipOpen(true);
      return;
    }
    setIsTooltipOpen(false);
  }, [text]);

  const content = (
    <Box
      ref={ref}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setIsTooltipOpen(false)}
      sx={{ minWidth: 0, width: "100%", overflow: "hidden" }}
    >
      {children}
    </Box>
  );

  return (
    <Tooltip
      title={tooltipText ?? ""}
      open={isTooltipOpen}
      onClose={() => setIsTooltipOpen(false)}
      placement="top"
      arrow
      disableFocusListener
      disableHoverListener
      disableTouchListener
      disableInteractive
      slotProps={TOOLTIP_SLOT_PROPS}
    >
      {content}
    </Tooltip>
  );
};

// Custom column definition type
export interface CustomColumnDef {
  field: string;
  headerName?: string;
  width?: number;
  flex?: number;
  sortable?: boolean;
  valueGetter?: (params: { row: any }) => any;
  disableTooltip?: boolean;
  tooltipValueGetter?: (params: { row: any; value: any }) => string;
  renderCell?: (params: CustomCellParams) => React.ReactNode;
  editable?: boolean;
  renderEditCell?: (params: CustomEditCellParams) => React.ReactNode;
}

// Custom cell parameters type
export interface CustomCellParams {
  row: any;
  field: string;
  value: any;
  /** renderExpandedRow 指定時のみ渡る。この行が展開中か */
  isExpanded?: boolean;
  /** renderExpandedRow 指定時のみ渡る。この行の展開トグル */
  toggleExpand?: () => void;
}

export interface CustomEditCellParams extends CustomCellParams {
  tempValue: any;
  setTempValue: (value: any) => void;
  commit: (value: any) => void;
}

export interface PaginationInfo {
  total: number;
  page: number;
  limit: number;
}

export interface TableProps {
  rows: any[];
  columns: CustomColumnDef[];
  onRowSelection?: (selectedIds: any[]) => void;
  selectedIds?: any[];
  isLoading?: boolean;
  logoUrl?: string;
  pagination?: PaginationInfo;
  checkBoxOpen?: boolean;
  onPageChange?: (page: number) => void;
  rowHeight?: number;
  hidePagination?: boolean;
  pageSize?: number;
  /** 値が変わったとき、テーブルを再マウントせず内部ページだけ1に戻す。 */
  pageResetKey?: React.Key;
  enableCheckboxSelection?: boolean;
  userRole?: string;
  noDataMessage?: string;
  noDataIcon?: React.ReactNode;
  minHeight?: string;
  enableColumnResize?: boolean;
  enableColumnReorder?: boolean;
  formMode?: boolean;
  onRowsChange?: (nextRows: any[]) => void;
  outlined?: boolean;
  initialSort?: { field: string; direction: "asc" | "desc" };
  getRowSx?: (row: any) => Record<string, any> | undefined;
  /**
   * 指定すると各行の下に全列幅の展開サブ行を表示する (工事履歴=RepairsTable 風)。
   * 展開状態はテーブル内部で管理し、renderCell の params に isExpanded/toggleExpand を渡す。
   * セル内に開閉アイコンを置きたい場合は params.toggleExpand を onClick に繋ぐ。
   */
  renderExpandedRow?: (row: any) => React.ReactNode;
  /** 初期展開する行 id (getRowId と同じ値) */
  defaultExpandedRowIds?: any[];
}

interface CustomNoRowsOverlayProps {
  noDataMessage: string;
  noDataIcon?: React.ReactNode;
}

const CustomNoRowsOverlay: React.FC<CustomNoRowsOverlayProps> = ({
  noDataMessage,
  noDataIcon,
}) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      py: 6,
      minHeight: "160px",
    }}
  >
    {noDataIcon ?? (
      <InboxOutlinedIcon
        sx={{ fontSize: 40, color: colors.border.light, mb: 1 }}
      />
    )}
    <Box
      sx={{
        textAlign: "center",
        color: colors.text.secondary,
        fontSize: "14px",
      }}
    >
      {noDataMessage}
    </Box>
  </Box>
);

const CustomLoadingOverlay: React.FC = () => (
  <Box
    sx={{
      position: "absolute",
      top: "36px",
      left: 0,
      right: 0,
      bottom: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "rgba(255, 255, 255, 1)",
      zIndex: 10,
      minHeight: "300px",
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
      }}
    >
      <Box
        sx={{
          width: "50px",
          height: "50px",
          border: "4px solid #f3f3f3",
          borderTop: "4px solid #0B9DBD",
          borderRadius: "50%",
          animation: `${spinAnimation} 1s linear infinite`,
        }}
      />
    </Box>
  </Box>
);

const MIN_COLUMN_WIDTH = 50;

const CustomTable: React.FC<TableProps> = ({
  rows,
  columns,
  onRowSelection,
  selectedIds = [],
  isLoading = false,
  pagination,
  checkBoxOpen = true,
  onPageChange,
  rowHeight = 42,
  hidePagination = false,
  pageSize: pageSizeProp,
  pageResetKey,
  enableCheckboxSelection = false,
  userRole,
  noDataMessage = "データが登録されていません",
  noDataIcon,
  minHeight = "400px",
  enableColumnResize = true,
  enableColumnReorder = true,
  formMode = false,
  onRowsChange,
  outlined = false,
  initialSort,
  getRowSx,
  renderExpandedRow,
  defaultExpandedRowIds,
}) => {
  const [localSelectedIds, setLocalSelectedIds] = useState<any[]>(selectedIds);
  const [localPage, setLocalPage] = useState(1);
  React.useEffect(() => {
    setLocalPage(1);
  }, [pageResetKey]);
  const [expandedRowIds, setExpandedRowIds] = useState<Set<any>>(
    () => new Set(defaultExpandedRowIds ?? []),
  );
  const toggleRowExpand = useCallback((id: any) => {
    setExpandedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const [isResizing, setIsResizing] = useState(false);
  const [resizeStartX, setResizeStartX] = useState(0);
  const [resizeInfo, setResizeInfo] = useState<{
    resizeColumn: string;
    resizeColumnWidth: number;
    rightColumns: { field: string; width: number }[];
  } | null>(null);
  const headerRowRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const manuallyResizedColumns = useRef<Set<string>>(new Set());

  const [draggedColumn, setDraggedColumn] = useState<string | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);
  const [columnOrder, setColumnOrder] = useState<string[]>(() =>
    columns.map((col) => col.field),
  );

  const [sort, setSort] = useState<{
    field: string | null;
    direction: "asc" | "desc" | null;
  }>({ field: null, direction: null });

  const computedInitialSort = useMemo(() => {
    if (initialSort && initialSort.field && initialSort.direction) {
      return initialSort;
    }
    const firstSortable = columns.find((c) => c.sortable !== false);
    if (firstSortable) {
      return { field: firstSortable.field, direction: "asc" as const };
    }
    return { field: null, direction: null } as const;
  }, [columns, initialSort]);

  React.useEffect(() => {
    if (!sort.field && computedInitialSort.field) {
      setSort({
        field: computedInitialSort.field,
        direction: computedInitialSort.direction,
      });
    }
  }, [computedInitialSort, sort.field]);

  const runDistribute = useCallback(() => {
    if (!containerRef.current) return;

    const headerFullWidth = containerRef.current.clientWidth;
    if (headerFullWidth <= 0) return;

    const hasCheckbox =
      checkBoxOpen && (enableCheckboxSelection || userRole === "admin");
    const checkboxWidth = hasCheckbox ? 48 : 0;
    const totalForColumns = headerFullWidth - checkboxWidth;

    const fixedCols = columns.filter((c) => c.width != null);
    const flexCols = columns.filter((c) => c.width == null);
    const fixedTotal = fixedCols.reduce((sum, c) => sum + (c.width ?? 0), 0);
    const remaining = Math.max(0, totalForColumns - fixedTotal);
    const totalFlex = flexCols.reduce((s, c) => s + (c.flex ?? 1), 0) || 1;

    const desired: Record<string, number> = {};
    columns.forEach((col) => {
      if (col.width != null) {
        desired[col.field] = col.width;
      } else {
        const flex = col.flex ?? 1;
        desired[col.field] = Math.max(
          MIN_COLUMN_WIDTH,
          Math.floor((remaining * flex) / totalFlex),
        );
      }
    });
    let flexSum = flexCols.reduce((s, c) => s + (desired[c.field] ?? 0), 0);
    const diff = remaining - flexSum;
    if (diff !== 0 && flexCols.length > 0) {
      const firstFlexField = flexCols[0].field;
      desired[firstFlexField] = Math.max(
        MIN_COLUMN_WIDTH,
        (desired[firstFlexField] ?? 0) + diff,
      );
      flexSum += diff;
    }

    const totalAssigned = fixedTotal + flexSum;
    if (totalAssigned > totalForColumns && flexSum > 0) {
      const maxFlexTotal = Math.max(0, totalForColumns - fixedTotal);
      const flexScale = maxFlexTotal / flexSum;
      flexCols.forEach((col) => {
        desired[col.field] = Math.max(
          MIN_COLUMN_WIDTH,
          Math.floor((desired[col.field] ?? 0) * flexScale),
        );
      });
      const newFlexSum = flexCols.reduce(
        (s, c) => s + (desired[c.field] ?? 0),
        0,
      );
      const finalDiff = maxFlexTotal - newFlexSum;
      if (finalDiff !== 0 && flexCols.length > 0) {
        desired[flexCols[0].field] = Math.max(
          MIN_COLUMN_WIDTH,
          (desired[flexCols[0].field] ?? 0) + finalDiff,
        );
      }
    }

    setColumnWidths((prev) => {
      const next: Record<string, number> = {};
      columns.forEach((col) => {
        if (
          manuallyResizedColumns.current.has(col.field) &&
          prev[col.field] != null
        ) {
          next[col.field] = prev[col.field];
        } else {
          next[col.field] = desired[col.field];
        }
      });

      // Ensure total does not exceed container width
      const currentTotal = columns.reduce(
        (sum, c) => sum + (next[c.field] ?? 0),
        0,
      );
      if (currentTotal > totalForColumns && totalForColumns > 0) {
        const nonFixedCols = columns.filter((c) => c.width == null);
        const fixedSum = columns
          .filter((c) => c.width != null)
          .reduce((sum, c) => sum + (next[c.field] ?? 0), 0);
        const availableForNonFixed = Math.max(0, totalForColumns - fixedSum);
        const nonFixedSum = nonFixedCols.reduce(
          (sum, c) => sum + (next[c.field] ?? 0),
          0,
        );
        if (nonFixedSum > 0) {
          const scale = availableForNonFixed / nonFixedSum;
          nonFixedCols.forEach((col) => {
            next[col.field] = Math.max(
              MIN_COLUMN_WIDTH,
              Math.floor((next[col.field] ?? 0) * scale),
            );
          });
          // Adjust rounding error on the first non-fixed column
          const newNonFixedSum = nonFixedCols.reduce(
            (sum, c) => sum + (next[c.field] ?? 0),
            0,
          );
          const roundingDiff = availableForNonFixed - newNonFixedSum;
          if (roundingDiff !== 0 && nonFixedCols.length > 0) {
            next[nonFixedCols[0].field] = Math.max(
              MIN_COLUMN_WIDTH,
              (next[nonFixedCols[0].field] ?? 0) + roundingDiff,
            );
          }
        }
      }

      // Ensure total fills the container when it grows (underflow)
      if (currentTotal < totalForColumns && totalForColumns > 0) {
        const shortage = totalForColumns - currentTotal;
        // Distribute extra space to non-fixed, non-manually-resized columns first
        const expandable = columns.filter(
          (c) =>
            c.width == null && !manuallyResizedColumns.current.has(c.field),
        );
        const targets =
          expandable.length > 0
            ? expandable
            : columns.filter((c) => c.width == null);
        if (targets.length > 0) {
          const perCol = Math.floor(shortage / targets.length);
          let remainder = shortage - perCol * targets.length;
          targets.forEach((col) => {
            const extra = perCol + (remainder > 0 ? 1 : 0);
            if (remainder > 0) remainder--;
            next[col.field] = (next[col.field] ?? 0) + extra;
          });
        }
      }

      const widthChanged = columns.some(
        (column) => prev[column.field] !== next[column.field],
      );
      return widthChanged ? next : prev;
    });
  }, [columns, checkBoxOpen, enableCheckboxSelection, userRole]);

  // useLayoutEffectでペイント前に列幅を計算（チラつき防止）
  useLayoutEffect(() => {
    runDistribute();
  }, [runDistribute]);

  React.useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      runDistribute();
    });
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [runDistribute]);

  const [editingValues, setEditingValues] = useState<Record<string, any>>({});
  const makeEditKey = useCallback(
    (rowObj: any, rowIndex: number, field: string) => {
      const id =
        (rowObj && (rowObj.id ?? rowObj._id ?? rowObj.key)) ?? rowIndex;
      return `${String(id)}::${field}`;
    },
    [],
  );

  const commitCellEdit = useCallback(
    (rowObj: any, rowIndex: number, field: string, value: any) => {
      if (!onRowsChange) return;
      const targetIndex = rows.findIndex((r) => r === rowObj);
      if (targetIndex === -1) return;
      const next = [...rows];
      next[targetIndex] = { ...next[targetIndex], [field]: value };
      onRowsChange(next);
      const key = makeEditKey(rowObj, rowIndex, field);
      setEditingValues((prev) => {
        const copy = { ...prev };
        delete copy[key];
        return copy;
      });
    },
    [rows, onRowsChange, makeEditKey],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent, columnField: string) => {
      e.preventDefault();
      const resizeColumnIndex = columnOrder.indexOf(columnField);
      const isLastColumn = resizeColumnIndex === columnOrder.length - 1;

      if (isLastColumn) {
        return;
      }

      const resizeColumnWidth =
        columnWidths[columnField] ||
        columns.find((c) => c.field === columnField)?.width ||
        150;

      // Capture widths of all columns to the right
      const rightColumns = columnOrder
        .slice(resizeColumnIndex + 1)
        .map((f) => ({
          field: f,
          width:
            columnWidths[f] || columns.find((c) => c.field === f)?.width || 150,
        }));

      setResizeInfo({
        resizeColumn: columnField,
        resizeColumnWidth,
        rightColumns,
      });

      setIsResizing(true);
      setResizeStartX(e.clientX);
    },
    [columnWidths, columns, columnOrder],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing || !resizeInfo) return;

      const deltaX = e.clientX - resizeStartX;
      const newResizeColumnWidth = Math.max(
        MIN_COLUMN_WIDTH,
        resizeInfo.resizeColumnWidth + deltaX,
      );
      const actualDelta = newResizeColumnWidth - resizeInfo.resizeColumnWidth;

      // Distribute the delta equally among right columns
      const rightCols = resizeInfo.rightColumns;
      const updates: Record<string, number> = {
        [resizeInfo.resizeColumn]: newResizeColumnWidth,
      };

      if (rightCols.length > 0 && actualDelta !== 0) {
        let remaining = actualDelta;

        if (actualDelta > 0) {
          // Shrinking right columns: loop to redistribute when columns hit MIN
          const currentWidths = new Map(
            rightCols.map((rc) => [rc.field, rc.width]),
          );
          const settled = new Set<string>();

          while (remaining > 0.5) {
            const active = rightCols.filter(
              (rc) =>
                !settled.has(rc.field) &&
                (currentWidths.get(rc.field) ?? 0) > MIN_COLUMN_WIDTH,
            );
            if (active.length === 0) break;

            const shrinkPerCol = remaining / active.length;
            let hitMin = false;

            for (const rc of active) {
              const curWidth = currentWidths.get(rc.field) ?? 0;
              const newWidth = Math.max(
                MIN_COLUMN_WIDTH,
                Math.round(curWidth - shrinkPerCol),
              );
              remaining -= curWidth - newWidth;
              currentWidths.set(rc.field, newWidth);
              if (newWidth <= MIN_COLUMN_WIDTH) {
                settled.add(rc.field);
                hitMin = true;
              }
            }

            if (!hitMin) break;
          }

          for (const rc of rightCols) {
            updates[rc.field] = currentWidths.get(rc.field) ?? MIN_COLUMN_WIDTH;
          }
        } else {
          // Expanding right columns: all participate equally
          const expandPerCol = -actualDelta / rightCols.length;
          for (const rc of rightCols) {
            updates[rc.field] = Math.round(rc.width + expandPerCol);
          }
          remaining = 0;
        }

        // If right columns can't shrink enough, cap the resize column
        if (remaining > 0) {
          updates[resizeInfo.resizeColumn] = Math.max(
            MIN_COLUMN_WIDTH,
            newResizeColumnWidth - remaining,
          );
        }
      }

      setColumnWidths((prev) => ({
        ...prev,
        ...updates,
      }));
    },
    [isResizing, resizeInfo, resizeStartX],
  );

  const handleMouseUp = useCallback(() => {
    if (resizeInfo) {
      manuallyResizedColumns.current.add(resizeInfo.resizeColumn);
      resizeInfo.rightColumns.forEach((rc) => {
        manuallyResizedColumns.current.add(rc.field);
      });
    }
    setIsResizing(false);
    setResizeInfo(null);
  }, [resizeInfo]);

  // ドラッグ&ドロップのハンドラー
  const handleDragStart = useCallback(
    (e: React.DragEvent, columnField: string) => {
      setDraggedColumn(columnField);
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/html", columnField);
    },
    [],
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent, columnField: string) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      setDragOverColumn(columnField);
    },
    [],
  );

  const handleDragLeave = useCallback(() => {
    setDragOverColumn(null);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent, targetColumn: string) => {
      e.preventDefault();

      if (!draggedColumn || draggedColumn === targetColumn) {
        setDraggedColumn(null);
        setDragOverColumn(null);
        return;
      }

      setColumnOrder((prev) => {
        const newOrder = [...prev];
        const draggedIndex = newOrder.indexOf(draggedColumn);
        const targetIndex = newOrder.indexOf(targetColumn);

        // ドラッグされた列を削除
        newOrder.splice(draggedIndex, 1);
        // ターゲット位置に挿入
        newOrder.splice(targetIndex, 0, draggedColumn);

        return newOrder;
      });

      setDraggedColumn(null);
      setDragOverColumn(null);
    },
    [draggedColumn],
  );

  // マウスイベントのリスナーを追加/削除
  React.useEffect(() => {
    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp]);

  const getRowId = (row: any, index: number) =>
    (row && (row.id ?? row._id ?? row.key)) ?? index;
  const handleHeaderSortClick = (field?: string, sortable: boolean = true) => {
    if (!field || !sortable) return;
    setSort((prev) => {
      if (prev.field !== field) return { field, direction: "asc" };
      if (prev.direction === "asc") return { field, direction: "desc" };
      return { field: null, direction: null };
    });
  };
  const sortedRows = useMemo(() => {
    if (!sort.field || !sort.direction) return rows;
    const col = columns.find((c) => c.field === sort.field);
    if (!col) return rows;
    const getValue = (row: any) => {
      const raw = col.valueGetter ? col.valueGetter({ row }) : row[col.field];
      return raw;
    };
    const result = [...rows].sort((a, b) => {
      const av = getValue(a);
      const bv = getValue(b);
      if (av == null && bv == null) return 0;
      if (av == null) return -1;
      if (bv == null) return 1;
      if (typeof av === "number" && typeof bv === "number") return av - bv;
      const as = String(av).toLowerCase();
      const bs = String(bv).toLowerCase();
      if (as < bs) return -1;
      if (as > bs) return 1;
      return 0;
    });
    return sort.direction === "desc" ? result.reverse() : result;
  }, [rows, columns, sort]);

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    if (pagination && onPageChange) {
      onPageChange(value);
    } else {
      setLocalPage(value);
    }
  };

  const pageSize = useMemo(
    () => (pagination ? pagination.limit : (pageSizeProp ?? 10)),
    [pagination, pageSizeProp],
  );

  const getPaginationValues = (): {
    totalPages: number;
    currentPage: number;
    showPagination: boolean;
  } => {
    if (pagination) {
      return {
        totalPages: Math.ceil(pagination.total / pagination.limit),
        currentPage: pagination.page,
        showPagination: pagination.total > pagination.limit,
      };
    } else {
      return {
        totalPages: Math.ceil(sortedRows.length / pageSize),
        currentPage: localPage,
        showPagination: sortedRows.length > pageSize,
      };
    }
  };

  const { totalPages, currentPage, showPagination } = getPaginationValues();

  // Determine if checkboxes should be shown
  const shouldShowCheckboxes = () => {
    if (!checkBoxOpen) return false;
    if (enableCheckboxSelection) return true;
    if (userRole === "admin") return true;
    return false;
  };
  const displayedRows = useMemo(() => {
    if (pagination) {
      return sortedRows;
    }
    // hidePaginationが有効な場合は全件表示
    if (hidePagination) {
      return sortedRows;
    }
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return sortedRows.slice(start, end);
  }, [sortedRows, pagination, currentPage, pageSize, hidePagination]);
  const allDisplayedIds = useMemo(
    () => displayedRows.map((r, i) => getRowId(r, i)),
    [displayedRows],
  );
  const isAllSelected = useMemo(
    () =>
      allDisplayedIds.length > 0 &&
      allDisplayedIds.every((id) => localSelectedIds.includes(id)),
    [allDisplayedIds, localSelectedIds],
  );
  const isIndeterminate = useMemo(
    () =>
      allDisplayedIds.some((id) => localSelectedIds.includes(id)) &&
      !isAllSelected,
    [allDisplayedIds, localSelectedIds, isAllSelected],
  );
  const toggleSelectAllDisplayed = () => {
    let next: any[];
    if (isAllSelected || isIndeterminate) {
      // すべて選択されている場合、またはindeterminate状態の場合は、すべてのチェックを外す
      next = localSelectedIds.filter((id) => !allDisplayedIds.includes(id));
    } else {
      // 何も選択されていない場合は、すべてのチェックを入れる
      const merged = new Set([...localSelectedIds, ...allDisplayedIds]);
      next = Array.from(merged);
    }
    setLocalSelectedIds(next);
    onRowSelection?.(next);
  };
  const toggleSelectOne = (row: any, index: number) => {
    const id = getRowId(row, index);
    const next = localSelectedIds.includes(id)
      ? localSelectedIds.filter((x) => x !== id)
      : [...localSelectedIds, id];
    setLocalSelectedIds(next);
    onRowSelection?.(next);
  };

  return (
    <>
      <Box
        ref={containerRef}
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
          overflow: "visible",
          minHeight: minHeight,
          border: outlined ? "1px solid #d9d9d9" : "none",
          borderRadius: outlined ? "8px" : undefined,
        }}
      >
        {isLoading && <CustomLoadingOverlay />}

        {/* Header */}
        <Box
          ref={headerRowRef}
          sx={{
            display: "flex",
            width: "100%",
            minWidth: 0,
            backgroundColor: "#F5F5F5",
            borderRadius: outlined ? "8px 8px 0 0" : "8px",
            height: "36px",
            alignItems: "center",
            border: outlined ? "none" : "1px solid rgba(224, 224, 224, 1)",
          }}
        >
          {shouldShowCheckboxes() && (
            <Box
              sx={{
                width: 48,
                minWidth: 48,
                flexShrink: 0,
                padding: "0 8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                position: "relative",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  right: 0,
                  top: "6px",
                  bottom: "6px",
                  width: "1px",
                  backgroundColor: "rgba(224, 224, 224, 1)",
                },
              }}
            >
              <CustomCheckbox
                checked={isAllSelected}
                indeterminate={isIndeterminate}
                onChange={toggleSelectAllDisplayed}
                size="extra-small"
              />
            </Box>
          )}

          {columnOrder.map((field, idx) => {
            const col = columns.find((c) => c.field === field);
            if (!col) return null;
            const isLastColumn = idx === columnOrder.length - 1;
            const sortable = col.sortable !== false;
            const active = sort.field === col.field ? sort.direction : null;
            const colWidth = columnWidths[col.field] ?? col.width ?? 150;
            return (
              <Box
                key={idx}
                draggable={enableColumnReorder}
                onDragStart={
                  enableColumnReorder
                    ? (e) => handleDragStart(e, col.field)
                    : undefined
                }
                onDragOver={
                  enableColumnReorder
                    ? (e) => handleDragOver(e, col.field)
                    : undefined
                }
                onDragLeave={enableColumnReorder ? handleDragLeave : undefined}
                onDrop={
                  enableColumnReorder
                    ? (e) => handleDrop(e, col.field)
                    : undefined
                }
                onClick={() => handleHeaderSortClick(col.field, sortable)}
                sx={{
                  width: colWidth,
                  flex: "0 0 auto",
                  flexShrink: 0,
                  minWidth: MIN_COLUMN_WIDTH,
                  padding:
                    isLastColumn && shouldShowCheckboxes()
                      ? "0 30px 0 12px"
                      : "0 16px 0 12px",
                  boxSizing: "border-box",
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                  position: "relative",
                  cursor: sortable ? "pointer" : "default",
                  opacity:
                    enableColumnReorder && draggedColumn === col.field
                      ? 0.5
                      : 1,
                  backgroundColor:
                    enableColumnReorder && dragOverColumn === col.field
                      ? "rgba(0, 0, 0, 0.05)"
                      : "transparent",
                  borderLeft:
                    enableColumnReorder && dragOverColumn === col.field
                      ? "2px solid #0B9DBD"
                      : "none",
                  "&:hover": {
                    backgroundColor: "transparent",
                  },
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    right: 0,
                    top: "6px",
                    bottom: "6px",
                    width: idx === columnOrder.length - 1 ? 0 : "1px",
                    backgroundColor: "rgba(224, 224, 224, 1)",
                  },
                }}
              >
                <Box
                  sx={{
                    fontWeight: 600,
                    fontSize: "14px",
                    color: "rgba(0, 0, 0, 0.87)",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    flex: 1,
                    minWidth: 0,
                    width: "100%",
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
                    <TruncatedTextWithTooltip
                      text={col.headerName ?? col.field}
                    />
                  </Box>
                  {active === "asc" && <TableHeaderUpArrowIcon />}
                  {active === "desc" && <TableHeaderDownArrowIcon />}
                </Box>
                {enableColumnResize && !isLastColumn && (
                  <Box
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      handleMouseDown(e, col.field);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      position: "absolute",
                      right: "-14px",
                      top: 0,
                      bottom: 0,
                      width: "28px",
                      cursor: "col-resize",
                      backgroundColor: "transparent",
                      zIndex: 1,
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        left: "50%",
                        top: 0,
                        bottom: 0,
                        width: "2px",
                        transform: "translateX(-50%)",
                        backgroundColor: "transparent",
                      },
                      "&:hover::after": {
                        backgroundColor: "rgba(0, 0, 0, 0.15)",
                      },
                      "&:active::after": {
                        backgroundColor: "rgba(0, 0, 0, 0.3)",
                      },
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Box>

        {/* Body */}
        <Box
          sx={{
            width: "100%",
            minWidth: 0,
            backgroundColor: "#fff",
            borderBottom: "none",
            minHeight: minHeight,
            borderTop: outlined ? "1px solid rgba(224, 224, 224, 1)" : "none",
          }}
        >
          {rows.length === 0 ? (
            <CustomNoRowsOverlay
              noDataMessage={noDataMessage}
              noDataIcon={noDataIcon}
            />
          ) : (
            displayedRows.map((row, rIndex) => {
              const rowId = getRowId(row, rIndex);
              const isRowExpanded = renderExpandedRow
                ? expandedRowIds.has(rowId)
                : false;
              return (
                <React.Fragment key={rowId}>
                  <Box
                    sx={{
                      display: "flex",
                      width: "100%",
                      minWidth: 0,
                      minHeight: `${rowHeight}px`,
                      backgroundColor: localSelectedIds.includes(rowId)
                        ? "#f0f8ff"
                        : "transparent",
                      borderBottom: isRowExpanded
                        ? "none"
                        : "1px solid rgba(224, 224, 224, 1)",
                      "&:hover": {
                        backgroundColor: "transparent",
                      },
                      ...(getRowSx ? getRowSx(row) : {}),
                    }}
                  >
                    {shouldShowCheckboxes() && (
                      <Box
                        sx={{
                          width: 48,
                          minWidth: 48,
                          flexShrink: 0,
                          padding: "8px 8px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <CustomCheckbox
                          checked={localSelectedIds.includes(
                            getRowId(row, rIndex),
                          )}
                          onChange={() => toggleSelectOne(row, rIndex)}
                          size="extra-small"
                        />
                      </Box>
                    )}

                    {columnOrder.map((field, cIndex) => {
                      const col = columns.find((c) => c.field === field);
                      if (!col) return null;
                      const cellColWidth =
                        columnWidths[col.field] ?? col.width ?? 150;
                      const value = col.valueGetter
                        ? col.valueGetter({ row })
                        : row[col.field];
                      const params: CustomCellParams = {
                        row,
                        field: col.field,
                        value,
                        ...(renderExpandedRow
                          ? {
                              isExpanded: isRowExpanded,
                              toggleExpand: () => toggleRowExpand(rowId),
                            }
                          : {}),
                      };
                      const tooltipValue = col.tooltipValueGetter?.({
                        row,
                        value,
                      });
                      const isLastCell = cIndex === columnOrder.length - 1;
                      return (
                        <Box
                          key={cIndex}
                          sx={{
                            width: cellColWidth,
                            flex: "0 0 auto",
                            flexShrink: 0,
                            minWidth: MIN_COLUMN_WIDTH,
                            padding:
                              isLastCell && shouldShowCheckboxes()
                                ? "4px 30px 4px 12px"
                                : "4px 16px 4px 12px",
                            boxSizing: "border-box",
                            display: "flex",
                            alignItems: "center",
                            overflow: "hidden",
                          }}
                        >
                          {formMode && col.editable ? (
                            col.renderEditCell ? (
                              col.renderEditCell({
                                ...params,
                                tempValue:
                                  editingValues[
                                    makeEditKey(row, rIndex, col.field)
                                  ] ?? value,
                                setTempValue: (v: any) =>
                                  setEditingValues((prev) => ({
                                    ...prev,
                                    [makeEditKey(row, rIndex, col.field)]: v,
                                  })),
                                commit: (v: any) =>
                                  commitCellEdit(row, rIndex, col.field, v),
                              })
                            ) : (
                              <TextField
                                value={
                                  editingValues[
                                    makeEditKey(row, rIndex, col.field)
                                  ] ?? value
                                }
                                onFocus={(
                                  _e: React.FocusEvent<
                                    HTMLInputElement | HTMLTextAreaElement
                                  >,
                                ) => {
                                  const key = makeEditKey(
                                    row,
                                    rIndex,
                                    col.field,
                                  );
                                  setEditingValues((prev) =>
                                    prev[key] === undefined
                                      ? { ...prev, [key]: value }
                                      : prev,
                                  );
                                }}
                                onChange={(
                                  e: React.ChangeEvent<
                                    HTMLInputElement | HTMLTextAreaElement
                                  >,
                                ) =>
                                  setEditingValues((prev) => ({
                                    ...prev,
                                    [makeEditKey(row, rIndex, col.field)]:
                                      e.target.value,
                                  }))
                                }
                                onBlur={(
                                  _e: React.FocusEvent<
                                    HTMLInputElement | HTMLTextAreaElement
                                  >,
                                ) => {
                                  const key = makeEditKey(
                                    row,
                                    rIndex,
                                    col.field,
                                  );
                                  const temp = editingValues[key];
                                  if (
                                    onRowsChange &&
                                    temp !== undefined &&
                                    temp !== value
                                  ) {
                                    commitCellEdit(
                                      row,
                                      rIndex,
                                      col.field,
                                      temp,
                                    );
                                  } else {
                                    // cleanup temp value when no change
                                    setEditingValues((prev) => {
                                      if (prev[key] === undefined) return prev;
                                      const copy = { ...prev };
                                      delete copy[key];
                                      return copy;
                                    });
                                  }
                                }}
                                variant="outlined"
                                size="small"
                                multiline
                                minRows={1}
                                maxRows={6}
                                sx={{
                                  width: "100%",
                                  "& .MuiOutlinedInput-root": {
                                    minHeight: "32px",
                                    borderRadius: "8px",
                                    alignItems: "center",
                                    padding: "3px 0 3px 9px",

                                    "& fieldset": { borderColor: "#D9D9D9" },
                                    "&:hover fieldset": {
                                      borderColor: "#D9D9D9",
                                    },
                                    "&.Mui-focused fieldset": {
                                      borderColor: "#D9D9D9 !important",
                                    },
                                    "&.Mui-focused": {
                                      outline: "none",
                                      boxShadow: "none",
                                    },
                                  },
                                  "& .MuiOutlinedInput-input": {
                                    fontSize: "12px",
                                    padding: "0px 0px 0px 0px",
                                    "&::placeholder": {
                                      fontSize: "12px",
                                      opacity: 0.8,
                                    },
                                  },
                                  "& .MuiOutlinedInput-inputMultiline": {
                                    fontSize: "12px",
                                    padding: "0px 0px 0px 0px",
                                  },
                                }}
                              />
                            )
                          ) : (
                            <Box
                              sx={{
                                minWidth: 0,
                                overflow: "hidden",
                                width: col.disableTooltip
                                  ? "max-content"
                                  : "100%",
                                flexShrink: col.disableTooltip ? 0 : undefined,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {col.renderCell ? (
                                col.disableTooltip ? (
                                  col.renderCell(params)
                                ) : (
                                  <CellTooltipWrapper text={tooltipValue}>
                                    {col.renderCell(params)}
                                  </CellTooltipWrapper>
                                )
                              ) : (
                                <Box
                                  sx={{
                                    width: "100%",
                                    minWidth: 0,
                                    fontSize: 14,
                                    fontWeight: 400,
                                    color: "#3e3e3e",
                                  }}
                                >
                                  <TruncatedTextWithTooltip
                                    text={String(value ?? "")}
                                  />
                                </Box>
                              )}
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                  {renderExpandedRow && (
                    <Collapse in={isRowExpanded} timeout="auto" unmountOnExit>
                      <Box
                        sx={{
                          px: 2,
                          py: 1,
                          backgroundColor: "#f9fafb",
                          borderBottom: "1px solid rgba(224, 224, 224, 1)",
                        }}
                      >
                        {renderExpandedRow(row)}
                      </Box>
                    </Collapse>
                  )}
                </React.Fragment>
              );
            })
          )}
        </Box>
      </Box>

      {!hidePagination && showPagination && totalPages > 1 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            mt: 3,
          }}
        >
          <Pagination
            shape="rounded"
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            disabled={isLoading}
            sx={{
              "& .MuiPaginationItem-root": {
                backgroundColor: "#F5F5F5",
                color: "#000",
                borderRadius: "6px",
                "&.Mui-selected": {
                  backgroundColor: "#039dbe",
                  color: "#fff",
                  "&:hover": {
                    backgroundColor: "#039dbe",
                  },
                },
                "&:hover": {
                  backgroundColor: "#DBF8FF",
                },
                "&.Mui-disabled": {
                  backgroundColor: "#F5F5F5",
                },
              },
            }}
          />
        </Box>
      )}
    </>
  );
};

export default CustomTable;
